import React, { useState } from 'react';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { api } from '../contexts/AuthContext';
import { useAuth } from '../contexts/AuthContext';
import { Download, FileText, ClipboardCopy, Building } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const monthNames = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const MAPA_HEADERS = [
  ['uf', 'UF'],
  ['municipio', 'MUNICIPIO'],
  ['tipo_aeronave', 'TIPO AERONAVE'],
  ['prefixo_aeronave', 'PREFIXO AERONAVE'],
  ['horas_aplicacao', 'HORAS APLICACAO'],
  ['cultura', 'CULTURA'],
  ['tipo_de_servico', 'TIPO DE SERVICO'],
  ['classe_agrotoxicos', 'CLASSE AGROTOXICOS'],
  ['classe_bioinsumo', 'CLASSE BIOINSUMO'],
  ['area_ha', 'AREA (ha)'],
  ['agrotoxico', 'AGROTOXICO'],
  ['bioinsumo', 'BIOINSUMO'],
  ['fertilizantes_adjuvantes_outros', 'FERTILIZANTES/ADJUVANTES/OUTROS'],
  ['semeadura', 'SEMEADURA'],
  ['combate_incendio_horas', 'COMBATE A INCENDIO (HORAS)'],
  ['volume_lha', 'VOLUME (l/ha)'],
  ['dosagem', 'DOSAGEM'],
  ['unidade', 'UNIDADE']
];

const toTsv = (rows) => {
  const escape = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return s.replace(/\t/g, ' ').replace(/\r?\n/g, ' ');
  };
  const headerLine = MAPA_HEADERS.map(([, label]) => escape(label)).join('\t');
  const lines = [headerLine];
  for (const r of rows || []) {
    lines.push(MAPA_HEADERS.map(([key]) => escape(r?.[key])).join('\t'));
  }
  return lines.join('\n');
};

const Reports = () => {
  const { user } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [orgFilter, setOrgFilter] = useState('');

  const { data: orgsData } = useQuery(
    'organizations',
    () => api.get('/admin/orgs').then(res => res.data),
    {
      enabled: user?.role === 'superadmin'
    }
  );

  const effectiveOrgFilter = user?.role === 'superadmin' ? orgFilter : user?.org_id;

  const monthsQuery = useQuery(
    ['reports', 'mapa', 'months', effectiveOrgFilter],
    async () => {
      const params = effectiveOrgFilter ? { org_id: effectiveOrgFilter } : {};
      const res = await api.get('/reports/mapa/months', { params });
      return res.data;
    },
    { keepPreviousData: true }
  );

  const mapaQuery = useQuery(
    ['reports', 'mapa', 'monthly', selectedMonth?.year, selectedMonth?.month, effectiveOrgFilter],
    async () => {
      if (!selectedMonth) return null;
      const params = { year: selectedMonth.year, month: selectedMonth.month };
      if (effectiveOrgFilter) params.org_id = effectiveOrgFilter;
      const res = await api.get('/reports/mapa/monthly', { params });
      return res.data;
    },
    { enabled: selectedMonth !== null, keepPreviousData: true }
  );

  const exportMapaCsv = async () => {
    if (!selectedMonth) return;
    try {
      const params = { year: selectedMonth.year, month: selectedMonth.month };
      if (effectiveOrgFilter) params.org_id = effectiveOrgFilter;
      const response = await api.get('/reports/mapa/export', { params, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mapa_${selectedMonth.year}_${String(selectedMonth.month).padStart(2, '0')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao exportar relatório MAPA');
    }
  };

  const downloadMonthCsv = async (year, month) => {
    try {
      const params = { year, month };
      if (effectiveOrgFilter) params.org_id = effectiveOrgFilter;
      const response = await api.get('/reports/mapa/export', { params, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mapa_${year}_${String(month).padStart(2, '0')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Relatório ${monthNames[month - 1]} ${year} baixado com sucesso!`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao baixar relatório');
    }
  };

  const copyMapa = async () => {
    const rows = mapaQuery.data?.rows || [];
    try {
      await navigator.clipboard.writeText(toTsv(rows));
      toast.success('Copiado. Cole direto na planilha oficial.');
    } catch {
      toast.error('Não foi possível copiar. Use o botão Exportar CSV.');
    }
  };

  const isLoading = monthsQuery.isLoading;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Relatórios</h1>
          <p className="text-gray-600">Geração mensal para envio ao MAPA</p>
        </div>
        {user?.role === 'superadmin' && (
          <div className="flex items-center space-x-2">
            <Building className="h-4 w-4 text-gray-400" />
            <select
              className="input"
              value={orgFilter}
              onChange={(e) => setOrgFilter(e.target.value)}
            >
              <option value="">Todas as Empresas</option>
              {orgsData?.map(org => (
                <option key={org.id} value={org.id}>{org.name || org.company_name || org.id}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <>
          {selectedMonth ? (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <button
                  onClick={() => setSelectedMonth(null)}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  ← Voltar para lista de meses
                </button>
              </div>
              <MapaMonthlyView
                data={mapaQuery.data}
                year={selectedMonth.year}
                month={selectedMonth.month}
                onExport={exportMapaCsv}
                onCopy={copyMapa}
                isLoading={mapaQuery.isLoading}
              />
            </div>
          ) : (
            <MapaMonthsList
              months={monthsQuery.data?.months || []}
              onMonthClick={setSelectedMonth}
              onDownload={downloadMonthCsv}
            />
          )}
        </>
      )}
    </div>
  );
};

const MapaMonthsList = ({ months, onMonthClick, onDownload }) => {
  if (months.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum dado encontrado</h3>
        <p className="text-gray-500">Não há operações registradas ainda.</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium text-gray-900">Relatórios disponíveis por mês</h3>
        <p className="text-sm text-gray-600">Total: {months.length} meses</p>
      </div>
      <div className="card-body">
        <div className="space-y-3">
          {months.map((m) => (
            <div key={`${m.year}-${m.month}`} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
              <div>
                <div className="font-medium text-gray-900">
                  {monthNames[m.month - 1]} {m.year}
                </div>
                <div className="text-sm text-gray-600">
                  {m.flight_count} operação{m.flight_count !== 1 ? 'ões' : ''} registrada{m.flight_count !== 1 ? 's' : ''}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => onMonthClick(m)}
                  className="btn-secondary text-sm"
                >
                  Ver detalhes
                </button>
                <button
                  onClick={() => onDownload(m.year, m.month)}
                  className="btn-primary text-sm"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Baixar CSV
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MapaMonthlyView = ({ data, year, month, onExport, onCopy, isLoading }) => {
  const rows = data?.rows || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum dado encontrado</h3>
        <p className="text-gray-500">Não há operações MAPA no período selecionado.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="card-body">
          <div className="flex gap-2">
            <button onClick={onCopy} className="btn-secondary">
              <ClipboardCopy className="h-4 w-4 mr-2" />
              Copiar (planilha)
            </button>
            <button onClick={onExport} className="btn-primary">
              <Download className="h-4 w-4 mr-2" />
              Exportar CSV (MAPA)
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Prévia (primeiras 20 linhas)</h3>
          <p className="text-sm text-gray-600">Total: {rows.length} linhas</p>
        </div>
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  {MAPA_HEADERS.map(([, label]) => (
                    <th key={label} className="table-header-cell whitespace-nowrap">{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="table-body">
                {rows.slice(0, 20).map((r, idx) => (
                  <tr key={idx}>
                    {MAPA_HEADERS.map(([key]) => (
                      <td key={key} className="table-cell whitespace-nowrap">{r?.[key] ?? ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
